/* eslint-disable react/prop-types */
/**
 * SalesHandoverInternalSection — v260.88
 * ======================================
 * Internal-only MEDDPIC + sales-artifact handover. Mounted inside the
 * Design Pack tab beneath the customer-facing sections. Visible ONLY
 * to admin/CS/CRO/CCO/super_admin (the same gate that protects the
 * backend `/api/estimates/{id}/sales-handover` endpoints).
 *
 * UX:
 *   • Defaults to read-only "view" mode showing whatever is saved.
 *   • An "Edit" button switches every sub-field to an input. Save
 *     fans out a single PUT; Cancel discards local edits.
 *   • Stays editable even AFTER the surrounding Design Pack is
 *     finalized — sales context evolves separately from delivery scope.
 *   • A persistent amber "Internal — not shared with customer" chip
 *     surfaces the data-sensitivity classification.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Lock, Loader2, Pencil, Save, X, Plus, Trash2, ExternalLink,
  Briefcase, Target, UserCheck, Swords, ShieldAlert, FileText,
  Link as LinkIcon, Video, Folder, FileDown,
} from 'lucide-react';
import { SectionCard } from './shared';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import logger from '../../../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_ROLES = new Set(['super_admin', 'admin', 'cs', 'cro', 'cco']);

const EMPTY = {
  meddpic: {
    metrics: [],
    economic_buyer: { name: '', email: '' },
    decision_criteria: [],
    decision_process: [],
    paper_process: [],
    identify_pain: [],
    champion: { name: '', email: '' },
    competition: '',
  },
  artifacts: {
    sales_experience: '',
    sales_content_folder_url: '',
    business_case_url: '',
    case_for_change_url: '',
    sales_recordings: [],
    hubspot_links: [],
  },
};

const SALES_EXPERIENCES = [
  { value: '', label: '—' },
  { value: 'guided', label: 'Guided User Experience' },
  { value: 'custom', label: 'Custom Demo' },
  { value: 'standard', label: 'Standard Demo' },
];

// Convert a "list of strings" between display ("one per line") and array form.
const linesToArray = (s) => (s || '').split('\n').map(x => x.trim()).filter(Boolean);

function BulletList({ items, empty = 'Not captured' }) {
  if (!items?.length) return <div className="text-xs text-slate-400 italic">{empty}</div>;
  return (
    <ul className="text-sm text-slate-700 list-disc list-inside space-y-0.5">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}

function ContactCard({ contact, label }) {
  if (!contact?.name && !contact?.email) {
    return <div className="text-xs text-slate-400 italic">No {label.toLowerCase()} on file</div>;
  }
  return (
    <div className="text-sm">
      <span className="font-medium text-[#0F2040]">{contact.name || '—'}</span>
      {contact.email && (
        <a
          href={`mailto:${contact.email}`}
          className="ml-2 text-xs text-sky-700 hover:underline"
        >
          {contact.email}
        </a>
      )}
    </div>
  );
}

function LinkList({ items, empty = 'No links captured' }) {
  if (!items?.length) return <div className="text-xs text-slate-400 italic">{empty}</div>;
  return (
    <ul className="space-y-1">
      {items.map((it, i) => (
        <li key={i} className="text-sm flex items-center gap-1.5">
          <ExternalLink className="w-3 h-3 text-slate-400 shrink-0" />
          <a
            href={it.url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sky-700 hover:underline truncate"
            title={it.url}
          >
            {it.label || it.url || 'Untitled link'}
          </a>
        </li>
      ))}
    </ul>
  );
}

function LinkRowEditor({ items, onChange, testidPrefix }) {
  const addRow = () => onChange([...(items || []), { label: '', url: '' }]);
  const updateRow = (idx, key, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: val };
    onChange(next);
  };
  const removeRow = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-1.5">
      {(items || []).map((row, idx) => (
        <div key={idx} className="flex items-center gap-1.5" data-testid={`${testidPrefix}-row-${idx}`}>
          <Input
            value={row.label || ''}
            onChange={(e) => updateRow(idx, 'label', e.target.value)}
            placeholder="Label (e.g. Discovery Call)"
            className="h-8 text-xs flex-shrink-0 w-48"
            data-testid={`${testidPrefix}-label-${idx}`}
          />
          <Input
            value={row.url || ''}
            onChange={(e) => updateRow(idx, 'url', e.target.value)}
            placeholder="https://…"
            className="h-8 text-xs flex-1"
            data-testid={`${testidPrefix}-url-${idx}`}
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => removeRow(idx)}
            className="h-8 w-8 text-slate-500 hover:text-rose-600"
            data-testid={`${testidPrefix}-remove-${idx}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={addRow}
        className="h-7 text-xs"
        data-testid={`${testidPrefix}-add-btn`}
      >
        <Plus className="w-3 h-3 mr-1" /> Add link
      </Button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">{label}</div>
      {children}
    </div>
  );
}

export default function SalesHandoverInternalSection({ estimateId, currentUser }) {
  const isAdmin = ADMIN_ROLES.has(currentUser?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/estimates/${estimateId}/sales-handover`,
        { withCredentials: true },
      );
      setData(r.data);
      setDraft({
        meddpic: { ...EMPTY.meddpic, ...(r.data?.meddpic || {}),
          economic_buyer: { ...EMPTY.meddpic.economic_buyer, ...((r.data?.meddpic || {}).economic_buyer || {}) },
          champion: { ...EMPTY.meddpic.champion, ...((r.data?.meddpic || {}).champion || {}) },
        },
        artifacts: { ...EMPTY.artifacts, ...(r.data?.artifacts || {}) },
      });
    } catch (e) {
      logger.warn('sales handover fetch failed', e?.message);
    } finally {
      setLoading(false);
    }
  }, [estimateId, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(
        `${API_URL}/api/estimates/${estimateId}/sales-handover`,
        draft,
        { withCredentials: true },
      );
      setData(r.data);
      setEditing(false);
      toast.success('Sales handover saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft({
      meddpic: { ...EMPTY.meddpic, ...(data?.meddpic || {}),
        economic_buyer: { ...EMPTY.meddpic.economic_buyer, ...((data?.meddpic || {}).economic_buyer || {}) },
        champion: { ...EMPTY.meddpic.champion, ...((data?.meddpic || {}).champion || {}) },
      },
      artifacts: { ...EMPTY.artifacts, ...(data?.artifacts || {}) },
    });
    setEditing(false);
  };

  // Non-admin viewers see nothing — section is hidden entirely.
  if (!isAdmin) return null;

  // Download the full Success Plan (PDF or Word). The .docx is editable
  // so internal staff can tweak before forwarding; the PDF is the
  // canonical read-only artifact. Both endpoints emit the same 9
  // sections and the same INTERNAL stripe.
  const downloadBriefing = async (format /* 'pdf' | 'docx' */) => {
    try {
      const ext = format === 'docx' ? 'docx' : 'pdf';
      const url = `${API_URL}/api/estimates/${estimateId}/sales-handover/briefing.${ext}`;
      const r = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const mime = ext === 'docx'
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : 'application/pdf';
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `success-plan-briefing.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`${ext.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Briefing download failed');
    }
  };

  const headerExtra = (
    <div className="flex items-center gap-2 ml-auto">
      <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
        <Lock className="w-2.5 h-2.5 mr-1" /> Internal · not shared with customer
      </Badge>
      {!editing && !loading && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadBriefing('pdf')}
            title="Download the full Success Plan as a print-ready PDF"
            data-testid="sales-handover-briefing-btn"
          >
            <FileDown className="w-3 h-3 mr-1.5" /> Success Plan · PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadBriefing('docx')}
            title="Download as an editable Word document"
            data-testid="sales-handover-briefing-docx-btn"
          >
            <FileDown className="w-3 h-3 mr-1.5" /> Word
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
            data-testid="sales-handover-edit-btn"
          >
            <Pencil className="w-3 h-3 mr-1.5" /> Edit
          </Button>
        </>
      )}
      {editing && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            disabled={saving}
            data-testid="sales-handover-cancel-btn"
          >
            <X className="w-3 h-3 mr-1.5" /> Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="sales-handover-save-btn"
          >
            {saving ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Save className="w-3 h-3 mr-1.5" />}
            Save
          </Button>
        </>
      )}
    </div>
  );

  return (
    <SectionCard
      id="sales-handover-internal"
      icon={Briefcase}
      title="Sales-to-Delivery Handover (Internal)"
      defaultOpen={false}
      titleExtra={headerExtra}
      testid="sales-handover-section"
    >
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : editing ? (
        <EditView draft={draft} setDraft={setDraft} />
      ) : (
        <ReadView data={data} />
      )}
      {!editing && data?.updated_at && (
        <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
          Last updated {new Date(data.updated_at).toLocaleString()} by{' '}
          <span className="font-medium text-[#0F2040]">{data.updated_by || '—'}</span>
        </div>
      )}
    </SectionCard>
  );
}

// ─── Read view ────────────────────────────────────────────────────────

function ReadView({ data }) {
  const m = data?.meddpic || EMPTY.meddpic;
  const a = data?.artifacts || EMPTY.artifacts;
  const experienceLabel = SALES_EXPERIENCES.find(s => s.value === a.sales_experience)?.label || '—';
  return (
    <div className="space-y-5" data-testid="sales-handover-read-view">
      {/* MEDDPIC */}
      <div>
        <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-slate-200">
          <Target className="w-3.5 h-3.5 text-[#0F2040]" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#0F2040]">MEDDPIC</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Metrics"><BulletList items={m.metrics} /></Field>
          <Field label="Economic Buyer">
            <ContactCard contact={m.economic_buyer} label="Economic Buyer" />
          </Field>
          <Field label="Decision Criteria"><BulletList items={m.decision_criteria} /></Field>
          <Field label="Decision Process"><BulletList items={m.decision_process} /></Field>
          <Field label="Paper Process"><BulletList items={m.paper_process} /></Field>
          <Field label="Identify Pain (sales view)"><BulletList items={m.identify_pain} /></Field>
          <Field label="Champion">
            <ContactCard contact={m.champion} label="Champion" />
          </Field>
          <Field label="Competition">
            <div className="text-sm text-slate-700">{m.competition || <span className="text-slate-400 italic">Not captured</span>}</div>
          </Field>
        </div>
      </div>

      {/* Sales artifacts */}
      <div>
        <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-slate-200">
          <FileText className="w-3.5 h-3.5 text-[#0F2040]" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#0F2040]">Sales Artifacts</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Sales Experience">
            <div className="text-sm text-slate-700">{experienceLabel}</div>
          </Field>
          <Field label="Sales Content Folder">
            {a.sales_content_folder_url ? (
              <a href={a.sales_content_folder_url} target="_blank" rel="noopener noreferrer" className="text-sm text-sky-700 hover:underline inline-flex items-center gap-1">
                <Folder className="w-3 h-3" /> Open folder
              </a>
            ) : <span className="text-xs text-slate-400 italic">Not linked</span>}
          </Field>
          <Field label="Business Case Document">
            {a.business_case_url ? (
              <a href={a.business_case_url} target="_blank" rel="noopener noreferrer" className="text-sm text-sky-700 hover:underline inline-flex items-center gap-1">
                <FileText className="w-3 h-3" /> Open document
              </a>
            ) : <span className="text-xs text-slate-400 italic">Not linked</span>}
          </Field>
          <Field label="Case for Change">
            {a.case_for_change_url ? (
              <a href={a.case_for_change_url} target="_blank" rel="noopener noreferrer" className="text-sm text-sky-700 hover:underline inline-flex items-center gap-1">
                <FileText className="w-3 h-3" /> Open document
              </a>
            ) : <span className="text-xs text-slate-400 italic">Not linked</span>}
          </Field>
          <Field label="Sales Recordings">
            <LinkList items={a.sales_recordings} empty="No recordings captured" />
          </Field>
          <Field label="HubSpot Links">
            <LinkList items={a.hubspot_links} empty="No HubSpot links" />
          </Field>
        </div>
      </div>
    </div>
  );
}

// ─── Edit view ────────────────────────────────────────────────────────

function EditView({ draft, setDraft }) {
  const setMed = (key, val) => setDraft(d => ({ ...d, meddpic: { ...d.meddpic, [key]: val } }));
  const setContact = (kind, key, val) => setDraft(d => ({
    ...d,
    meddpic: { ...d.meddpic, [kind]: { ...d.meddpic[kind], [key]: val } },
  }));
  const setArt = (key, val) => setDraft(d => ({ ...d, artifacts: { ...d.artifacts, [key]: val } }));

  return (
    <div className="space-y-5" data-testid="sales-handover-edit-view">
      {/* MEDDPIC */}
      <div>
        <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-slate-200">
          <Target className="w-3.5 h-3.5 text-[#0F2040]" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#0F2040]">MEDDPIC</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Metrics (one per line)">
            <Textarea
              value={(draft.meddpic.metrics || []).join('\n')}
              onChange={(e) => setMed('metrics', linesToArray(e.target.value))}
              rows={3}
              className="text-xs"
              placeholder="Replace Arlo and Moodle&#10;Reduce manual operational overhead"
              data-testid="sales-handover-metrics-input"
            />
          </Field>
          <Field label="Economic Buyer">
            <div className="grid grid-cols-2 gap-1.5">
              <Input
                value={draft.meddpic.economic_buyer.name || ''}
                onChange={(e) => setContact('economic_buyer', 'name', e.target.value)}
                placeholder="Name"
                className="h-8 text-xs"
                data-testid="sales-handover-econbuyer-name"
              />
              <Input
                value={draft.meddpic.economic_buyer.email || ''}
                onChange={(e) => setContact('economic_buyer', 'email', e.target.value)}
                placeholder="email@company.com"
                className="h-8 text-xs"
                data-testid="sales-handover-econbuyer-email"
              />
            </div>
          </Field>
          <Field label="Decision Criteria (one per line)">
            <Textarea
              value={(draft.meddpic.decision_criteria || []).join('\n')}
              onChange={(e) => setMed('decision_criteria', linesToArray(e.target.value))}
              rows={3}
              className="text-xs"
              data-testid="sales-handover-decision-criteria-input"
            />
          </Field>
          <Field label="Decision Process (one per line)">
            <Textarea
              value={(draft.meddpic.decision_process || []).join('\n')}
              onChange={(e) => setMed('decision_process', linesToArray(e.target.value))}
              rows={3}
              className="text-xs"
              data-testid="sales-handover-decision-process-input"
            />
          </Field>
          <Field label="Paper Process (one per line)">
            <Textarea
              value={(draft.meddpic.paper_process || []).join('\n')}
              onChange={(e) => setMed('paper_process', linesToArray(e.target.value))}
              rows={3}
              className="text-xs"
              data-testid="sales-handover-paper-process-input"
            />
          </Field>
          <Field label="Identify Pain (one per line, sales view)">
            <Textarea
              value={(draft.meddpic.identify_pain || []).join('\n')}
              onChange={(e) => setMed('identify_pain', linesToArray(e.target.value))}
              rows={3}
              className="text-xs"
              data-testid="sales-handover-identify-pain-input"
            />
          </Field>
          <Field label="Champion">
            <div className="grid grid-cols-2 gap-1.5">
              <Input
                value={draft.meddpic.champion.name || ''}
                onChange={(e) => setContact('champion', 'name', e.target.value)}
                placeholder="Name"
                className="h-8 text-xs"
                data-testid="sales-handover-champion-name"
              />
              <Input
                value={draft.meddpic.champion.email || ''}
                onChange={(e) => setContact('champion', 'email', e.target.value)}
                placeholder="email@company.com"
                className="h-8 text-xs"
                data-testid="sales-handover-champion-email"
              />
            </div>
          </Field>
          <Field label="Competition">
            <Input
              value={draft.meddpic.competition || ''}
              onChange={(e) => setMed('competition', e.target.value)}
              placeholder="e.g. Intellum"
              className="h-8 text-xs"
              data-testid="sales-handover-competition-input"
            />
          </Field>
        </div>
      </div>

      {/* Sales artifacts */}
      <div>
        <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-slate-200">
          <FileText className="w-3.5 h-3.5 text-[#0F2040]" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#0F2040]">Sales Artifacts</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Sales Experience">
            <select
              value={draft.artifacts.sales_experience || ''}
              onChange={(e) => setArt('sales_experience', e.target.value)}
              className="h-8 text-xs border rounded px-2 bg-white w-full"
              data-testid="sales-handover-experience-select"
            >
              {SALES_EXPERIENCES.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Sales Content Folder URL">
            <Input
              value={draft.artifacts.sales_content_folder_url || ''}
              onChange={(e) => setArt('sales_content_folder_url', e.target.value)}
              placeholder="https://drive.google.com/…"
              className="h-8 text-xs"
              data-testid="sales-handover-content-folder-input"
            />
          </Field>
          <Field label="Business Case Document URL">
            <Input
              value={draft.artifacts.business_case_url || ''}
              onChange={(e) => setArt('business_case_url', e.target.value)}
              placeholder="https://…"
              className="h-8 text-xs"
              data-testid="sales-handover-business-case-input"
            />
          </Field>
          <Field label="Case for Change URL">
            <Input
              value={draft.artifacts.case_for_change_url || ''}
              onChange={(e) => setArt('case_for_change_url', e.target.value)}
              placeholder="https://…"
              className="h-8 text-xs"
              data-testid="sales-handover-case-for-change-input"
            />
          </Field>
          <Field label="Sales Recordings">
            <LinkRowEditor
              items={draft.artifacts.sales_recordings}
              onChange={(items) => setArt('sales_recordings', items)}
              testidPrefix="sales-handover-recordings"
            />
          </Field>
          <Field label="HubSpot Links">
            <LinkRowEditor
              items={draft.artifacts.hubspot_links}
              onChange={(items) => setArt('hubspot_links', items)}
              testidPrefix="sales-handover-hubspot"
            />
          </Field>
        </div>
      </div>
    </div>
  );
}
